import React, { useState, useRef, useEffect } from "react";
import { Button, TextField, CircularProgress, Drawer, List, ListItem, ListItemText, IconButton } from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import { GoX } from "react-icons/go";
import { IoMdAdd } from "react-icons/io";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import "./SimpleChat.css";
import AddIcon from '@mui/icons-material/Add';
import ReactMarkdown from 'react-markdown';


const SimpleChat = () => {
  const [messages, setMessages] = useState([]); 
  const [inputText, setInputText] = useState(""); 
  const [file, setFile] = useState(null); 
  const [isTyping, setIsTyping] = useState(false); 
  const [isUploading, setIsUploading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [fileInfo, setFileInfo] = useState(null);
  const [uploadedFiles, setuploadedFiles] = useState([]);

  // useEffect(() => {
  //   setUniqueId(uuidv4());
  // }, []);
  
  // Reference to message container for auto-scrolling
  const messageContainerRef = useRef(null);
  
  // File input reference for reset functionality
  const fileInputRef = useRef(null);
  const fileInputDrawerRef = useRef(null);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (messageContainerRef.current) {
      messageContainerRef.current.scrollTop = messageContainerRef.current.scrollHeight;
    }
  }, [messages]);

  const getAllUploadedFiles = async(sid) => {
    try{
      const response = await fetch(`https://demo.kaptize.ai:8080/get_files/${sid}`, {
        method: 'GET',
      });

      if (!response.ok) {
        throw new Error(`failed to get files`);
      }
      
      const responseData = await response.json();
      
      if (responseData.status === "success") {
        setuploadedFiles(responseData.data.files);
      }
    }catch(error){
      console.log(error);
    }
  };

  // Function to upload a file to the API
  const uploadFile = async (files) => {
    try {
      setIsUploading(true);
      
      // Create FormData for file upload
      const formData = new FormData();
      Array.from(files).forEach(file => {
        formData.append('files', file);
      });
      
      // Add system message for better UX
      setMessages(prev => [
        ...prev,
        { 
          id: Date.now(), 
          text: `Uploading ${files.length} file(s)...`, 
          isUser: false 
        }
      ]);
      
      // Call the upload API endpoint
      const response = await fetch('https://demo.kaptize.ai:8080/upload', {
        method: 'POST',
        body: formData,
      });
      
      if (!response.ok) {
        throw new Error(`Upload failed with status: ${response.status}`);
      }
      
      const responseData = await response.json();
      
      if (responseData.status === "success") {
        // console.log("Upload successful:", responseData.data);
        const sid = responseData.data.session_id;
        setSessionId(sid);
        getAllUploadedFiles(sid);
        setFileInfo({
          rows: responseData.data.rows,
          columns: responseData.data.columns
        });
        
        // Add success message
        setMessages(prev => [
          ...prev,
          { 
            id: Date.now(), 
            text: `Files uploaded successfully.`, 
            isUser: false 
          }
        ]);
        
        setIsUploading(false);
        return responseData.data;
      } else {
        throw new Error(responseData.responseMessage || "Upload failed");
      }
    } catch (error) {
      setIsUploading(false);
      console.error('Error uploading files:', error);
      
      setMessages(prev => [
        ...prev,
        { 
          id: Date.now(), 
          text: `Error uploading files: ${error.message}`, 
          isUser: false 
        }
      ]);
      
      return null;
    }
  };

  // Function to send a question to the API
  const askQuestion = async (question, sid) => {
    try {
      setIsTyping(true);
      setInputText("");
      // Use the provided session ID or the state
      const currentSessionId = sid || sessionId;
      
      // Prepare request body - make session_id optional
      const requestBody = {
        question: question
      };
      
      // Only add session_id if it exists
      if (currentSessionId) {
        requestBody.session_id = currentSessionId;
      }
      
      console.log("Sending question", currentSessionId ? "with session ID: " + currentSessionId : "without session ID");
      
      // Call the ask API endpoint
      const response = await fetch('https://demo.kaptize.ai:8080/ask', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });
      
      if (!response.ok) {
        throw new Error(`Request failed with status: ${response.status}`);
      }
      
      const responseData = await response.json();
      
      if (responseData.status === "success") {
        // Add AI response to messages
        setMessages(prev => [
          ...prev,
          { 
            id: Date.now(), 
            text: responseData.data.answer || "No answer was provided.", 
            isUser: false,
            sources: responseData.data.sources || []
          }
        ]);
        
      } else {
        throw new Error(responseData.responseMessage || "Failed to get an answer");
      }
    } catch (error) {
      console.error('Error asking question:', error);
      
      setMessages(prev => [
        ...prev,
        { 
          id: Date.now(), 
          text: `Error: ${error.message}`, 
          isUser: false 
        }
      ]);
    } finally {
      setIsTyping(false);
    }
  };

  // Function to handle sending a message
  const handleSendMessage = async () => {
    if (inputText.trim()) {
      // Create unique ID for message
      const msgId = Date.now();
      
      // Create and add user message
      const newMessage = {
        id: msgId,
        text: inputText.trim(),
        isUser: true,
      };
      
      // Update messages state with user message
      setMessages(prev => [...prev, newMessage]);
      
      // Process the question using current session ID
      await askQuestion(inputText.trim());
      
      // Clear input field 
      setInputText("");
    } 
  };

  // Handle file selection with automatic upload
  const handleFileChange = async (e) => {
    const selectedFiles = e.target.files;
    if (selectedFiles && selectedFiles.length > 0) {
      await uploadFile(selectedFiles);
    }
  };

  // Handle input change
  const handleInputChange = (e) => {
    setInputText(e.target.value);
  };

  // Handle Enter key press to send message
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Reset the chat
  const resetChat = () => {
    setMessages([]);
    setSessionId(null);
    setFileInfo(null);
    setFile(null);
    setInputText("");
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Add new state for sidebar
  const [sidebarOpen, setSidebarOpen] = useState(true);
 
  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleAddFile = () => {
    if (fileInputDrawerRef.current) {
      fileInputDrawerRef.current.click();
    }
  };

  const handleConfirmAddFiles = async (e) => {
    const selectedFiles = e.target.files;
    if (selectedFiles && selectedFiles.length > 0) {
      setIsUploading(true);
      try {
        // Create FormData for file upload
        const formData = new FormData();
        Array.from(selectedFiles).forEach(file => {
          formData.append('files', file);
        });
        formData.append('session_id', sessionId);
        
        // Add system message for better UX
        setMessages(prev => [
          ...prev,
          { 
            id: Date.now(), 
            text: `Uploading ${selectedFiles.length} file(s)...`, 
            isUser: false 
          }
        ]);
        
        // Call the upload API endpoint
        const response = await fetch('https://demo.kaptize.ai:8080/add_file', {
          method: 'POST',
          body: formData,
        });
        
        if (!response.ok) {
          throw new Error(`Upload failed with status: ${response.status}`);
        }
        
        const responseData = await response.json();
        
        if (responseData.status === "success") {
          console.log("Upload successful:", responseData.data);
          setuploadedFiles(responseData.data.sources);
          
          // Add success message
          setMessages(prev => [
            ...prev,
            { 
              id: Date.now(), 
              text: `Files added successfully.`, 
              isUser: false 
            }
          ]);
          
          return responseData.data;
        } else {
          throw new Error(responseData.responseMessage || "Upload failed");
        }
      } catch (error) {
        console.error('Error adding files:', error);
        
        setMessages(prev => [
          ...prev,
          { 
            id: Date.now(), 
            text: `Error adding files: ${error.message}`, 
            isUser: false 
          }
        ]);
      } finally {
        setIsUploading(false); // Make sure to set loading state to false when done
      }
    }
  };

  return (
    <div className="flex h-screen">
      {/* Sidebar */}
      <Drawer
        variant="persistent"
        anchor="left"
        open={sidebarOpen}
        sx={{
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: 250,
            boxSizing: 'border-box',
          },
        }}
      >
        <div className="flex items-center justify-between p-2 border-b">
          <div className="flex items-center gap-3">
            
          <h3 className="font-semibold text-lg">Files</h3>
          {uploadedFiles.length >0 &&
          <IconButton onClick={handleAddFile} className="bg-red-500">
            <AddIcon className="addIcon"/>
          </IconButton>
          }
          </div>
          {/* <IconButton onClick={toggleSidebar}>
            <ChevronLeftIcon />
          </IconButton> */}
        </div>
        <List>
  {uploadedFiles.map((file, index) => (
    <ListItem 
      key={index}
      button
      className="hover:bg-gray-100"
    >
      <InsertDriveFileIcon className="mr-2 text-gray-600" />
      <ListItemText primary={file} />
    </ListItem>
  ))}
</List>
<input 
  type="file" 
  onChange={handleConfirmAddFiles} 
  style={{ display: "none" }} 
  ref={fileInputDrawerRef}
  disabled={isUploading || isTyping}
  multiple
/>
      </Drawer>

      {/* Main Chat Container */}
      <div 
  className="flex-1 flex flex-col"
  style={{ marginLeft: sidebarOpen ? '250px' : '0', transition: 'margin-left 0.3s' }}
>
        {/* Chat Header */}
        <div className="flex py-3 items-center px-4 justify-between border-b">
          <div className="flex items-center gap-2">
            {(uploadedFiles.length >0 || !isUploading) &&  
            <IconButton onClick={toggleSidebar}>
              <MenuIcon />
            </IconButton>
            }
            <img
              src="images\ovaledge-new-logo.svg"
              alt="Logo" 
              className="chatlogo"
            />
          </div>
          <button onClick={resetChat}>
            <GoX className="closeicon" />
          </button>
        </div>

      {/* Messages Section */}
      <div className="messageSection" ref={messageContainerRef}>
      {messages.length === 0 && (
  <div className="emptyState">
    <div className="emptyStateContent">
  <h1 className="text-2xl text-black font-semi-bold">Welcome!</h1>
  <div className="mt-2 text-gray-700">We're glad you're here at OvalEdge Data Chat</div>
  <div className="mt-1 text-gray-700">Feel free to ask any questions or upload a file!</div>
</div>

  </div>
)}
        
        {messages.map((message) => (
  <div key={message.id} className={`messageRow ${message.isUser ? "userMessageRow" : "botMessageRow"}`}>
    <div className={`message ${message.isUser ? "userMessage" : "botMessage"}`}>
      {!message.isUser && <div className="botAvatar">AI</div>}
      {message.text && (
        <div className={message.isUser ? "userResponse" : "botResponse"}>
          <ReactMarkdown>{message.text}</ReactMarkdown>

          {/* Conditionally render sources */}
          {message.sources && message.sources.length > 0 && (
            <div className="sourceList mt-4 text-xm">
              <strong>Sources:</strong>
              <ul className="list-disc list-inside ml-4 mt-1">
                {message.sources.map((src, idx) => (
                  <li key={idx}>{src}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
      {message.file && (
        <span className={message.isUser ? "userResponse" : "botResponse"}>
          📎 {message.file}
        </span>
      )}
    </div>
  </div>
))}



        {/* Typing Indicator */}
        {isTyping && (
          <div className="messageRow botMessageRow">
            <div className="message botMessage">
              <div className="botAvatar">AI</div>
              <div className="typingIndicator">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>
        )}
        
        {/* Uploading Indicator */}
        {isUploading && (
          <div className="messageRow botMessageRow">
            <div className="message botMessage">
              <div className="botAvatar">AI</div>
              <div className="uploadingIndicator">
                <CircularProgress size={20} className="uploadSpinner" />
                <span>Uploading file...</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Input Section */}
      <div className="inputContainer">
        {!sessionId && (
          <label htmlFor="file-input" className="fileButton">
            <IoMdAdd className="addIcon" />
          </label>
        )}
        <input 
          id="file-input" 
          type="file" 
          onChange={handleFileChange} 
          style={{ display: "none" }} 
          ref={fileInputRef}
          multiple // Add this attribute
          disabled={isUploading || isTyping}
        />

<TextField
  id="standard-basic"
  type="text"
  value={inputText}
  label={sessionId ? "Ask about your data" : "Ask a question"}
  onChange={handleInputChange}
  onKeyDown={handleKeyDown}
  placeholder={sessionId ? "What would you like to know about your data?" : "What would you like to know?"}
  variant="outlined"
  className="chatInput"
  disabled={isUploading || isTyping}
  multiline
  maxRows={3}
/>

<Button 
  className={`sendButton ${(!inputText.trim() || isUploading) ? "disabled" : ""}`}
  onClick={handleSendMessage}
  disabled={isUploading || !inputText.trim() || isTyping}
>
  <SendIcon className="sendIcon" />
</Button>
      </div>
      </div>
    </div>
  );
};

export default SimpleChat;